import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ErrorNotice from "../../../errors/ErrorNotice";
import { chosenProjectDecision, useProjects } from "../../../projects/projectContext";
import type { DocumentVersionRef } from "../../../shared/references/contentReferences";
import LoadingIndicator from "../../../ui/feedback/LoadingIndicator";
import { useThreadPreparation } from "../../threads/prepare";
import { formatCollectionVersionRef } from "../../../shared/references/contentReferences";
import { useCorpusSelection } from "../corpus/corpusSelectionContext";
import { appPaths } from "../../../shared/routing/appPaths";
import DocumentHeader from "./DocumentHeader";
import DocumentView from "./DocumentView";
import SegmentFocus from "./SegmentFocus";
import { documentDisplayName } from "./documentNames";
import { useSourceDocument } from "./useSourceDocument";

interface DocumentAtVersionProps {
  reference: DocumentVersionRef;
  /** The segment the address names, shown on its own above the document. */
  segmentPath: string | null;
}

/**
 * One exact document version. The data model or a Thread beside it is the split view's job
 * (F26, WA-10). A focused segment shows that segment alone, with its way back to the whole
 * document (F27, W-09); the header's one start action works on it while it is focused, and on
 * the whole document otherwise.
 */
export default function DocumentAtVersion({ reference, segmentPath }: DocumentAtVersionProps) {
  const { t } = useTranslation();
  const projects = useProjects();
  const projectId = projects.selectedProjectId;
  const preparation = useThreadPreparation();
  // The corpus that holds this document, as the server reads it; work started here binds it (BA16-07).
  const { corpora } = useCorpusSelection();
  const { documentId } = reference;
  const { state: document, reload } = useSourceDocument(reference);

  let documentContent: ReactNode;
  if (document.status === "loading") {
    documentContent = <LoadingIndicator label={t("sources.document.loading")} className="py-10" />;
  } else if (document.status === "failed") {
    documentContent = <ErrorNotice error={document.error} onRetry={reload} className="p-4" />;
  } else {
    documentContent = segmentPath === null ? <DocumentView document={document.value} projectId={projectId} /> : null;
  }
  const documentKey = document.status === "loaded" ? documentDisplayName(document.value) : null;
  const holdingCorpusId = document.status === "loaded" ? document.value.corpusId : null;
  const holdingCorpus = corpora?.status === "loaded" ? (corpora.value.find((corpus) => corpus.corpusId === holdingCorpusId) ?? null) : null;
  return (
    <div className="space-y-4">
      <DocumentHeader
        reference={reference}
        documentKey={documentKey}
        corpusVersion={holdingCorpus?.latestVersion ?? null}
        segmentFocused={segmentPath !== null}
        starting={chosenProjectDecision(projects, "propose_change")}
        onStartThread={projectId === null || holdingCorpus === null ? null : () => {
          preparation.prepare({
            kind: "run",
            projectId,
            returnTo: appPaths.sourceDocument(reference, segmentPath),
            workflow: null,
            entries: [{ document: reference, label: documentKey ?? documentId, ...(segmentPath === null ? {} : { segmentPath }) }],
            corpusVersion: formatCollectionVersionRef(holdingCorpus.latestVersion),
          });
        }}
      />
      {segmentPath === null ? null : <SegmentFocus document={reference} segmentPath={segmentPath} />}
      {documentContent}
    </div>
  );
}
